import { Celebration } from "../../calendar/ordinariate-calendar";
import { CalendarDay } from "../../calendar/utils";
import { LiturgyItem } from "../../liturgy/types";
import { PointedText } from "../../models";
import { LoadableDataDependency } from "../../utils/RemoteDataProvider";
import { LessonData, Settings } from "../../utils/settings";
import { NewHymnData } from "./NewHymn";

export interface OrdinariateLessons {
    lessons: {
        morningFirstLesson: LessonData;
        morningSecondLesson: LessonData;
        eveningFirstLesson: LessonData;
        eveningSecondLesson: LessonData;
    };
    psalms: [
        {
            title: string;
            psalms: {
                morning: {
                    ref: string;
                    sections: {
                        verseNumber: number;
                        pointedText: PointedText;
                        toneId: string;
                    }[];
                }[];
                evening: {
                    ref: string;
                    sections: {
                        verseNumber: number;
                        pointedText: PointedText;
                        toneId: string;
                    }[];
                }[];
            };
        }
    ];
    nunc?: PointedText;
    venite?: string;
    invAnts: { [antId: string]: { gabc: string; tone: string } };
    otCanticleOptions: {
        title: string;
        tone: string;
        pointedText?: PointedText;
        gabc?: string;
    }[];
    hymns: { [hymnId: string]: NewHymnData };
    bens: {
        [antId: string]: {
            ant: { gabc: string; tone: string };
            simplifiedAnt: { gabc: string; tone: string };
            ben: PointedText;
            simplifiedBen?: PointedText;
            onlySimplified: boolean;
        };
    };
    mags: {
        [antId: string]: {
            ant: { gabc: string; tone: string };
            simplifiedAnt: { gabc: string; tone: string };
            mag: PointedText;
            simplifiedMag?: PointedText;
            onlySimplified: boolean;
        };
    };
}

export const capitalizeGabcFirstWord = (gabc: string) => {
    let isInsideNeume = false;

    let charsSoFar = 0;
    let wordsSoFar = 0;
    let result = "";

    for (let charIndex = 0; charIndex < gabc.length; charIndex++) {
        const char = gabc[charIndex];

        if (char === ")") {
            isInsideNeume = false;
        }

        if (isInsideNeume) {
            result += char;
        } else {
            if (char === " " && charsSoFar > 1) {
                wordsSoFar++;
            }

            if (
                "abcdefghijklmnopqrstuvwxyz".includes(
                    char.toLocaleLowerCase()
                ) &&
                wordsSoFar < 1
            ) {
                charsSoFar++;
                result += char.toLocaleUpperCase();
            } else {
                result += char;
            }
        }

        if (char === "(") {
            isInsideNeume = true;
        }
    }
    return result;
};

export const capitalizeFirstWord = (line: string) => {
    if (line.split(" ")[0].length === 1) {
        return (
            line.split(" ")[0].toLocaleUpperCase() +
            " " +
            line.split(" ")[1].toLocaleUpperCase() +
            " " +
            line.split(" ").slice(2).join(" ")
        );
    } else {
        return (
            line.split(" ")[0].toLocaleUpperCase() +
            " " +
            line.split(" ").slice(1).join(" ")
        );
    }
};

export const getOfficeOfTheDead = (
    day: CalendarDay,
    hour: "first-evensong" | "mattins" | "second-evensong",
    celebration: Celebration,
    settings: Settings,
    loadableLessons: LoadableDataDependency<OrdinariateLessons>,
    selectedOtCanticle: string,
    onSelectOtCanticle: (newSelection: string) => void,
    officiantType: string,
    onSelectOfficiantType: (newSelection: string) => void,
    antiphonOption: string,
    onSelectAntiphonOption: (newOption: string) => void,
    selectedCommon: string,
    onSelectCommon: (newCommon: string) => void,
    selectedPsalms: string,
    onSelectPsalms: (newPsalms: string) => void,
    isLent: boolean,
    isEastertide: boolean,
    simplifiedHaecDies: string,
    onSelectSimplifiedHaecDies: (newOption: string) => void,
    includeIntroduction: string,
    onSelectIncludeIntroduction: (newOption: string) => void
) => {
    console.log("celebration", celebration);
    console.log(loadableLessons);

    const firstLesson =
        loadableLessons.state === "loaded"
            ? hour === "mattins"
                ? loadableLessons.value.lessons.morningFirstLesson
                : loadableLessons.value.lessons.eveningFirstLesson
            : undefined;

    const secondLesson =
        loadableLessons.state === "loaded"
            ? hour === "mattins"
                ? loadableLessons.value.lessons.morningSecondLesson
                : loadableLessons.value.lessons.eveningSecondLesson
            : undefined;

    const items: LiturgyItem[] = [];

    items.push({
        type: "rubric",
        content:
            "At the Office of the Dead the usual introductory responses are omitted.",
    });

    items.push({
        type: "rubric",
        content: "The following Psalmody is said immediately.",
    });

    if (hour === "mattins") {
        if (loadableLessons.state === "loaded") {
            const invAnt = loadableLessons.value.invAnts["ood inv"];

            if (loadableLessons.value.venite && invAnt) {
                items.push({
                    type: "gabc chant",
                    title: "Antiphon",
                    gabc: invAnt.gabc,
                    tone: invAnt.tone,
                    dropCap: true,
                    playButtons: [
                        {
                            gabc: invAnt.gabc,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc: invAnt.gabc,
                            title: "Play full",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "gabc chant",
                    title: day.date === 19 ? "Jubliate Deo" : "Venite",
                    gabc: loadableLessons.value.venite,
                    tone: "VI",
                    dropCap: true,
                    playButtons: [
                        {
                            gabc: loadableLessons.value.venite,
                            title: "Intone",
                            intoneOnly: true,
                        },
                        {
                            gabc:
                                loadableLessons.value.venite.split("(::)")[0] +
                                "(::)",
                            title: "Play line",
                            intoneOnly: false,
                        },
                    ],
                });

                items.push({
                    type: "gabc chant",
                    title: "Antiphon",
                    gabc: invAnt.gabc,
                    tone: invAnt.tone,
                    dropCap: true,
                    playButtons: [],
                });

                items.push({ type: "spacer" });
            }
        }
    }

    const psalmTitles: { [id: string]: string } = {
        "ood make thy way": "Psalm 5",
        "ood turn thee o lord": "Psalm 6",
        "ood lest he devour": "Psalm 7",
        "ood from the gate": "Ego dixi",
        "ood i will walk": "Psalm 116",
        "ood woe is me": "Psalm 120",
        "ood the lord shall": "Psalm 121",
        "ood if thou lord": "Psalm 130",
        "ood despise not": "Psalm 138",
        "ood i am the": "Benedictus",
        "ood all that the": "Magnificat",
    };

    if (loadableLessons.state === "loaded") {
        (hour === "mattins"
            ? ["ood make thy way", "ood turn thee o lord", "ood lest he devour"]
            : [
                  "ood i will walk",
                  "ood woe is me",
                  "ood the lord shall",
                  "ood if thou lord",
                  "ood despise not",
              ]
        ).forEach((id) => {
            if (loadableLessons.state === "loaded") {
                const psalm = loadableLessons.value.bens[id];

                if (psalm) {
                    items.push({
                        type: "picker",
                        options: ["Full", "Simplified"],
                        onChange: onSelectAntiphonOption,
                        selectedOption: antiphonOption,
                    });

                    const psalmAnt =
                        antiphonOption === "Full" && !psalm.onlySimplified
                            ? psalm.ant
                            : psalm.simplifiedAnt;

                    items.push({
                        type: "gabc chant",
                        title: "Antiphon",
                        gabc: psalmAnt.gabc,
                        tone: psalmAnt.tone,
                        dropCap: true,
                        playButtons: [
                            {
                                gabc: psalmAnt.gabc,
                                title: "Intone",
                                intoneOnly: true,
                            },
                            {
                                gabc: psalmAnt.gabc,
                                title: "Play full",
                                intoneOnly: false,
                            },
                        ],
                    });

                    items.push({
                        type: "tone chant",
                        pointedText:
                            (antiphonOption === "Simplified" ||
                                psalm.onlySimplified) &&
                            psalm.simplifiedBen
                                ? psalm.simplifiedBen
                                : psalm.ben,
                        tone:
                            antiphonOption === "Full"
                                ? psalm.ant.tone
                                : psalm.ant.tone.replace("trans", ""),
                        boldMode: "alternate",
                        title: psalmTitles[id] ?? "",
                    });

                    items.push({
                        type: "gabc chant",
                        title: "Antiphon",
                        gabc: psalmAnt.gabc,
                        tone: psalmAnt.tone,
                        dropCap: true,
                        playButtons: [
                            {
                                gabc: psalmAnt.gabc,
                                title: "Intone",
                                intoneOnly: true,
                            },
                            {
                                gabc: psalmAnt.gabc,
                                title: "Play full",
                                intoneOnly: false,
                            },
                        ],
                    });

                    items.push({ type: "spacer" });
                }
            }
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: "The **First_Lesson** is_read.",
    });

    if (firstLesson !== undefined && Array.isArray(firstLesson)) {
        firstLesson.forEach(
            (lessonPart: { title: string; content: any }, index) => {
                items.push({
                    type: "scripture",
                    title: lessonPart.title,
                    content: lessonPart.content,
                    hasBeginneth: index === 0,
                });
            }
        );

        items.push({
            type: "text",
            content: "Here endeth the first lesson.",
        });
    } else {
        items.push({ type: "loading" });
    }

    if (hour !== "mattins") {
        items.push({
            type: "gabc chant",
            title: "Versicle & Response",
            gabc: "(c4) I(h) heard(h) a(h) voice(h) from(h) hea(h)ven,(h) say(h)ing(h) un(i)to(h) me.(g) (::) *Bless*(h)*ed*(h) *are*(h) *the*(h) *dead*(h) *which*(h) *die*(i) *in*(h) *the*(h) *Lord.*(g) (::)",
            dropCap: true,
            tone: "",
            playButtons: [
                {
                    gabc: "(c3) x(h) x(i) x(h) x(g)",
                    title: "Play",
                    intoneOnly: false,
                },
            ],
        });
    }

    items.push({ type: "spacer" });

    if (loadableLessons.state === "loaded") {
        (hour === "mattins"
            ? ["ood from the gate"]
            : ["ood all that the"]
        ).forEach((id) => {
            if (loadableLessons.state === "loaded") {
                const psalm = loadableLessons.value.bens[id];

                if (psalm) {
                    items.push({
                        type: "picker",
                        options: ["Full", "Simplified"],
                        onChange: onSelectAntiphonOption,
                        selectedOption: antiphonOption,
                    });

                    const psalmAnt =
                        antiphonOption === "Full" && !psalm.onlySimplified
                            ? psalm.ant
                            : psalm.simplifiedAnt;

                    items.push({
                        type: "gabc chant",
                        title: "Antiphon",
                        gabc: psalmAnt.gabc,
                        tone: psalmAnt.tone,
                        dropCap: true,
                        playButtons: [
                            {
                                gabc: psalmAnt.gabc,
                                title: "Intone",
                                intoneOnly: true,
                            },
                            {
                                gabc: psalmAnt.gabc,
                                title: "Play full",
                                intoneOnly: false,
                            },
                        ],
                    });

                    items.push({
                        type: "tone chant",
                        pointedText:
                            (antiphonOption === "Simplified" ||
                                psalm.onlySimplified) &&
                            psalm.simplifiedBen
                                ? psalm.simplifiedBen
                                : psalm.ben,
                        tone:
                            antiphonOption === "Full"
                                ? psalm.ant.tone
                                : psalm.ant.tone.replace("trans", ""),
                        boldMode: "alternate",
                        title: psalmTitles[id] ?? "",
                    });

                    items.push({
                        type: "gabc chant",
                        title: "Antiphon",
                        gabc: psalmAnt.gabc,
                        tone: psalmAnt.tone,
                        dropCap: true,
                        playButtons: [
                            {
                                gabc: psalmAnt.gabc,
                                title: "Intone",
                                intoneOnly: true,
                            },
                            {
                                gabc: psalmAnt.gabc,
                                title: "Play full",
                                intoneOnly: false,
                            },
                        ],
                    });

                    items.push({ type: "spacer" });
                }
            }
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({
        type: "rubric",
        content: "The **Second_Lesson** is_read.",
    });

    if (secondLesson !== undefined && Array.isArray(secondLesson)) {
        secondLesson.forEach(
            (lessonPart: { title: string; content: any }, index) => {
                items.push({
                    type: "scripture",
                    title: lessonPart.title,
                    content: lessonPart.content,
                    hasBeginneth: index === 0,
                });
            }
        );

        items.push({
            type: "text",
            content: "Here endeth the second lesson.",
        });
    } else {
        items.push({ type: "loading" });
    }

    items.push({ type: "spacer" });

    if (hour === "mattins") {
        // Mattins so add the hymn and benedictus

        items.push({
            type: "gabc chant",
            title: "Versicle & Response",
            gabc: "(c4) I(h) heard(h) a(h) voice(h) from(h) hea(h)ven,(h) say(h)ing(h) un(i)to(h) me.(g) (::) *Bless*(h)*ed*(h) *are*(h) *the*(h) *dead*(h) *which*(h) *die*(i) *in*(h) *the*(h) *Lord.*(g) (::)",
            dropCap: true,
            tone: "",
            playButtons: [
                {
                    gabc: "(c3) x(h) x(i) x(h) x(g)",
                    title: "Play",
                    intoneOnly: false,
                },
            ],
        });

        items.push({ type: "spacer" });

        items.push({
            type: "rubric",
            content:
                "The **Benedictus** is recited, and may be accompanied by the appointed Antiphon.",
        });

        if (loadableLessons.state === "loaded") {
            ["ood i am the"].forEach((id) => {
                if (loadableLessons.state === "loaded") {
                    const psalm = loadableLessons.value.bens[id];

                    if (psalm) {
                        items.push({
                            type: "picker",
                            options: ["Full", "Simplified"],
                            onChange: onSelectAntiphonOption,
                            selectedOption: antiphonOption,
                        });

                        const psalmAnt =
                            antiphonOption === "Full" && !psalm.onlySimplified
                                ? psalm.ant
                                : psalm.simplifiedAnt;

                        items.push({
                            type: "gabc chant",
                            title: "Antiphon",
                            gabc: psalmAnt.gabc,
                            tone: psalmAnt.tone,
                            dropCap: true,
                            playButtons: [
                                {
                                    gabc: psalmAnt.gabc,
                                    title: "Intone",
                                    intoneOnly: true,
                                },
                                {
                                    gabc: psalmAnt.gabc,
                                    title: "Play full",
                                    intoneOnly: false,
                                },
                            ],
                        });

                        items.push({
                            type: "tone chant",
                            pointedText:
                                (antiphonOption === "Simplified" ||
                                    psalm.onlySimplified) &&
                                psalm.simplifiedBen
                                    ? psalm.simplifiedBen
                                    : psalm.ben,
                            tone:
                                antiphonOption === "Full"
                                    ? psalm.ant.tone
                                    : psalm.ant.tone.replace("trans", ""),
                            boldMode: "alternate",
                            title: psalmTitles[id] ?? "",
                        });

                        items.push({
                            type: "gabc chant",
                            title: "Antiphon",
                            gabc: psalmAnt.gabc,
                            tone: psalmAnt.tone,
                            dropCap: true,
                            playButtons: [
                                {
                                    gabc: psalmAnt.gabc,
                                    title: "Intone",
                                    intoneOnly: true,
                                },
                                {
                                    gabc: psalmAnt.gabc,
                                    title: "Play full",
                                    intoneOnly: false,
                                },
                            ],
                        });

                        items.push({ type: "spacer" });
                    }
                }
            });
        } else {
            items.push({ type: "loading" });
        }
    }

    if (hour !== "mattins") {
        // Evensong so add the nunc

        items.push({
            type: "rubric",
            content: "The **Nunc Dimittis** is recited.",
        });

        if (loadableLessons.state === "loaded") {
            if (loadableLessons.value.otCanticleOptions) {
                const selectedCanticle =
                    loadableLessons.value.otCanticleOptions[2];

                if (selectedCanticle) {
                    if (selectedCanticle.gabc) {
                        items.push({
                            type: "gabc chant",
                            title: selectedCanticle.title,
                            tone: selectedCanticle.tone,
                            gabc: selectedCanticle.gabc,
                            dropCap: true,
                            playButtons: [
                                {
                                    gabc: selectedCanticle.gabc,
                                    title: "Intone",
                                    intoneOnly: true,
                                },
                            ],
                        });
                    } else if (selectedCanticle.pointedText) {
                        items.push({
                            type: "tone chant",
                            title: selectedCanticle.title,
                            pointedText: selectedCanticle.pointedText,
                            tone: selectedCanticle.tone,
                            boldMode: "alternate",
                        });
                    }
                }

                items.push({ type: "spacer" });
            }
        } else {
            items.push({ type: "loading" });
        }
    }

    items.push({
        type: "rubric",
        content:
            "Then the Officiant and People shall say the **Lord's prayer,** all devoutly kneeling.",
    });

    items.push({
        type: "text",
        content:
            "O**UR Father, who art in heaven, hallowed be thy Name, thy kingdom come, thy will be done, on earth as it is in heaven. Give us this day our daily bread. And forgive us our trespasses, as we forgive those who trespass against us. And lead us not into temptation, but deliver us from evil. Amen.**",
        dropCap: true,
    });

    items.push({
        type: "rubric",
        content:
            "This Psalm is then said, except on the day of death or burial.",
    });
    if (loadableLessons.state === "loaded") {
        if (loadableLessons.value.otCanticleOptions) {
            const selectedCanticle =
                loadableLessons.value.otCanticleOptions[
                    hour === "mattins" ? 0 : 1
                ];

            if (selectedCanticle) {
                if (selectedCanticle.gabc) {
                    items.push({
                        type: "gabc chant",
                        title: selectedCanticle.title,
                        tone: selectedCanticle.tone,
                        gabc: selectedCanticle.gabc,
                        dropCap: true,
                        playButtons: [
                            {
                                gabc: selectedCanticle.gabc,
                                title: "Intone",
                                intoneOnly: true,
                            },
                        ],
                    });
                } else if (selectedCanticle.pointedText) {
                    items.push({
                        type: "tone chant",
                        title: selectedCanticle.title,
                        pointedText: selectedCanticle.pointedText,
                        tone: selectedCanticle.tone,
                        boldMode: "alternate",
                    });
                }
            }

            items.push({ type: "spacer" });
        }
    } else {
        items.push({ type: "loading" });
    }

    items.push({
        type: "rubric",
        content: "Then the Office continues as follows.",
    });
    items.push({
        type: "picker",
        options: ["Lay officiant", "Priest officiant"],
        selectedOption: officiantType,
        onChange: onSelectOfficiantType,
    });

    if (officiantType === "Priest officiant") {
        items.push({
            type: "minimal gabc chant",
            gabc: "(c3) From(h) the(h) gate(g) of(f) hell.(g) (::) *De*(h)*li*(h)*ver*(h) *{their*(h) *souls},*(h) *O*(f) *Lord.*(f) (::) May(h) {they}(h) rest(g) in(f) peace.(g) (::) *A*(h)*men.*(h) (::) The(h) Lord(g) be(f) with(g) you.(g) (::) *And*(h) *with*(h) *thy*(h) *spi*(h)*rit.*(f) (::) Let(h) us(h) pray.(h) (::)",
            dropCap: true,
            tone: "",
        });
    } else {
        items.push({
            type: "minimal gabc chant",
            gabc: "(c3) From(h) the(h) gate(g) of(f) hell.(g) (::) *De*(h)*li*(h)*ver*(h) *{their*(h) *souls}*(h) *O*(f) *Lord.*(f) (::) May(h) {they}(h) rest(g) in(f) peace.(g) (::) *A*(h)*men.*(h) (::) O(h) Lord(h) hear(g) our(f) prayer.(g) (::) *And*(h) *let*(h) *our*(h) *cry*(h) *come*(h) *un*(h)*to*(f) *thee.*(f) (::) Let(h) us(h) pray.(h) (::)",
            dropCap: true,
            tone: "",
        });
    }

    items.push({
        type: "rubric",
        content: `This, or another appropriate **Collect** (DW:DO_pp._448-451) is_then_sung_recto_tono. **SEE BELOW.**`,
    });
    items.push({
        type: "text",
        content:
            "O GOD, the Creator and Redeemer of all them that believe: grant unto the souls of thy servants and handmaids the remission of all their sins; that as they have ever desired thy merciful pardon, so by the supplications of their brethren they may receive the same; through Jesus Christ thy Son our Lord, who livest and reignest with thee, in the unity of the Holy Spirit, ever one God, world without end. **Amen.**",
        dropCap: true,
    });

    items.push({
        type: "rubric",
        content: `**For a Pope.**`,
    });
    items.push({
        type: "text",
        content:
            "O God, who of thy ineffable providence didst vouchsafe to number thy servant Pope Francis in the company of thy high Priests: grant, we beseech thee; that as he did fulfil on earth the office of thine Only Begotten Son, he may be numbered in the everlasting fellowship of thy holy Pontiffs; through the same Jesus Christ thy Son our Lord, who livest and reignest with thee, in the unity of the Holy Spirit, ever one God, world without end. **Amen.**",
        dropCap: true,
    });

    if (officiantType === "Priest officiant") {
        items.push({
            type: "minimal gabc chant",
            gabc: "(c3) The(h) Lord(g) be(f) with(g) you.(g) (::) *And*(h) *with*(h) *thy*(h) *spi*(h)*rit.*(f) (::) Rest(f) e(f)ter(f)nal(f) grant(f) un(f)to(f) them,(f) O(f) Lord.(f) (::) *And*(f) *let*(f) *light*(f) *per*(f)*pe*(f)*tual*(f) *shine*(f) *u*(f)*pon*(f) *them.*(f) (::) May(e) they(f) rest(f) in(e) peace.(fe) (::) *A*(e)*men.*(ef) (::)",
            dropCap: true,
            tone: "",
        });
    } else {
        items.push({
            type: "minimal gabc chant",
            gabc: "(c3) O(h) Lord(h) hear(g) our(f) prayer.(g) (::) *And*(h) *let*(h) *our*(h) *cry*(h) *come*(h) *un*(h)*to*(f) *thee.*(f) (::) Rest(f) e(f)ter(f)nal(f) grant(f) un(f)to(f) them,(f) O(f) Lord.(f) (::) *And*(f) *let*(f) *light*(f) *per*(f)*pe*(f)*tual*(f) *shine*(f) *u*(f)*pon*(f) *them.*(f) (::) May(e) they(f) rest(f) in(e) peace.(fe) (::) *A*(e)*men.*(ef) (::)",
            dropCap: true,
            tone: "",
        });
    }

    items.push({ type: "spacer" });

    items.push({
        type: "rubric",
        content: `Here ends the Order of ${
            hour === "mattins" ? "Morning" : "Evening"
        } Prayer.`,
    });

    items.push({ type: "spacer", big: true });

    return items;
};
